import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ExternalLink, Mail } from "lucide-react";
import { GCSB_LINKS } from "@/data/policiesV2";

export const PGDCSL_URL = "/courses/pgdcsl-postgraduate-executive-diploma-converged-security-leadership";

export const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--gold-deep)]";

const Ext = ({ href, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={`text-[var(--terracotta)] font-semibold hover:underline inline-flex items-center gap-1 rounded ${FOCUS}`} data-testid={testid}>
    {children} <ExternalLink size={12} aria-hidden="true"/><span className="sr-only">(opens in a new tab)</span>
  </a>
);

const RULES = [
  ["Five steps to the diploma", "CSCE, CSP, CSL, CCSO and a final Executive Integration Portfolio."],
  ["Study at your own pace", "Most candidates plan 24-36 months; your registration lasts five years."],
  ["Experience counts", "With 15+ years in senior roles you can apply for executive entry, reviewed by an independent panel. The final portfolio is always part of the diploma."],
  ["Fair, independent assessment", "The portfolio is marked out of 100 (pass mark 70) by an assessor and an independent moderator, with Distinction, Merit and Pass results."],
  ["90 GCSB Academic Credits", "Shown on your transcript. They are professional credits, not university credits unless a formal agreement recognises them."],
  ["Stay current with CPD", "25 hours a year after graduation, submitted online at gcsb.uk/cpd."],
  ["A right to appeal", "You can appeal an assessment or recognition decision within 30 calendar days."],
];

export const KeyRules = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" aria-labelledby="key-rules-h" data-testid="policies-key-rules">
    <div className="eyebrow mb-1">In short</div>
    <h2 id="key-rules-h" className="font-display text-2xl sm:text-3xl mb-5">What you need to know</h2>
    <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4">
      {RULES.map(([k, v], i) => (
        <li key={k} className="flex gap-3" data-testid={`key-rule-${i}`}>
          <CheckCircle2 size={18} className="text-[var(--gold-deep)] shrink-0 mt-0.5" aria-hidden="true"/>
          <p className="text-sm text-zinc-700 leading-relaxed"><b className="text-[var(--navy)]">{k}.</b> {v}</p>
        </li>
      ))}
    </ul>
    <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
      <Link to={PGDCSL_URL} className={`btn-terracotta px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 ${FOCUS}`} data-testid="policies-start-pgdcsl-btn">
        Start your PGDCSL journey <ArrowRight size={14} aria-hidden="true"/>
      </Link>
      <span className="text-xs text-zinc-500">Full details are in the manual above and the <Ext href="https://gcsb.uk/standards" testid="rules-standards-link">GCSB standards</Ext>.</span>
    </div>
  </section>
);

export const Contacts = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
    <div className="rounded-3xl bg-brand-navy text-white p-8 sm:p-12">
      <div className="eyebrow text-[var(--gold-soft)]">Questions, appeals and complaints</div>
      <h2 className="font-display text-3xl sm:text-4xl mt-2">Who to contact</h2>
      <div className="grid md:grid-cols-2 gap-6 mt-6 text-sm text-zinc-200">
        <div>
          <h3 className="font-semibold text-white mb-1">CSI learning delivery and service complaints</h3>
          <p>Teaching, customer service and delivery matters go first to CSI as the delivery provider. Your statutory rights and the applicable provider terms are not affected.</p>
          <a href="mailto:info@convergedsecurity.es?subject=CSI%20Delivery%20Enquiry" className={`btn-terracotta mt-3 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${FOCUS}`} data-testid="policies-contact-btn">
            <Mail size={14} aria-hidden="true"/> Email CSI: info@convergedsecurity.es
          </a>
        </div>
        <div>
          <h3 className="font-semibold text-white mb-1">GCSB qualification governance and credential concerns</h3>
          <p>Assessment, recognition, award and conduct appeals use GCSB's independent route, within 30 calendar days of notification under GCSB-STD-005.</p>
          <a href="https://gcsb.uk/contact" target="_blank" rel="noopener noreferrer" className={`chip mt-3 px-5 py-2.5 bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2 ${FOCUS}`} data-testid="policies-gcsb-contact-btn">
            Contact GCSB <ExternalLink size={13} aria-hidden="true"/><span className="sr-only">(opens in a new tab)</span>
          </a>
        </div>
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-8 text-sm" aria-label="GCSB resources">
        {GCSB_LINKS.map(([l, h]) => (
          <li key={h}><a href={h} target="_blank" rel="noopener noreferrer" className={`text-[var(--gold-soft)] hover:underline rounded ${FOCUS}`} data-testid={`gcsb-link-${h.split("/").pop()}`}>{l} ↗<span className="sr-only"> (opens in a new tab)</span></a></li>
        ))}
      </ul>
    </div>
  </section>
);
